import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import parseCoords from 'coord-parser';
import debounce from 'debounce-promise';
import {func, string} from 'prop-types';
import React, {useCallback, useEffect, useState} from 'react';

const base = 'https://api.mapbox.com/geocoding/v5/mapbox.places';
const accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

const geocode = debounce(async function (text) {
  const coords = coordsFromString(text);
  let search;
  if (coords) {
    search = coords.join(',');
  } else {
    search = encodeURIComponent(text);
  }
  const url = `${base}/${search}.json?access_token=${accessToken}`;
  const response = await fetch(url);
  if (!response.ok) {
    return [];
  }

  const results = await response.json();
  const features = results.features || [];

  if (!coords) {
    return features;
  }

  features.unshift({
    type: 'Feature',
    id: 'point.' + coords.join(','),
    geometry: {
      type: 'Point',
      coordinates: coords,
    },
    properties: {},
    place_name: formatCoords(coords),
  });
  return features;
}, 250);

function isOptionEqualToValue(option, value) {
  return option.id === value.id;
}

function getOptionLabel(option) {
  return option.place_name;
}

function round(num, decimals) {
  const factor = Math.pow(10, decimals);
  return Math.round(num * factor) / factor;
}

function formatCoords(coords) {
  const ew = coords[0] < 0 ? 'W' : 'E';
  const ns = coords[1] < 0 ? 'S' : 'N';
  const lat = round(Math.abs(coords[1]), 4);
  const lon = round(Math.abs(coords[0]), 4);
  return `${lat}° ${ns}, ${lon}° ${ew}`;
}

function filterOptions(options) {
  return options;
}

function coordsFromString(string) {
  let coords = null;
  try {
    const obj = parseCoords(string);
    if (obj.lat >= -90 && obj.lat <= 90 && obj.lon >= -180 && obj.lon <= 180) {
      coords = [round(obj.lon, 8), round(obj.lat, 8)];
    }
  } catch {
    // pass;
  }
  return coords;
}

function Geocoder({onChange, label = 'Search', ...props}) {
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let active = true;
    if (inputValue.length < 2) {
      return;
    }

    (async () => {
      const features = await geocode(inputValue);
      if (active) {
        if (value) {
          if (!features.some(feature => isOptionEqualToValue(feature, value))) {
            features.push(value);
          }
        }
        setOptions(features);
      }
    })();

    return () => {
      active = false;
    };
  }, [inputValue, value]);

  const onInputChange = useCallback((event, newInputValue) => {
    if (event.type === 'change') {
      setInputValue(newInputValue);
    }
  }, []);

  const onChangeInternal = useCallback(
    (event, newValue) => {
      if (!newValue) {
        setOptions([]);
      }
      setValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    },
    [onChange],
  );

  return (
    <Autocomplete
      autoHighlight
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      filterOptions={filterOptions}
      filterSelectedOptions
      value={value}
      options={options}
      renderInput={params => (
        <TextField
          {...params}
          sx={{backgroundColor: 'background.paper'}}
          label={label}
        />
      )}
      onInputChange={onInputChange}
      onChange={onChangeInternal}
      {...props}
    />
  );
}

Geocoder.propTypes = {
  onChange: func.isRequired,
  label: string,
};

export default Geocoder;
